'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import VehicleCard from './VehicleCard';
import FilterSidebar from './FilterSidebar';
import VehicleDetailModal from './VehicleDetailModal';
import QuickQuoteCalculator from './QuickQuoteCalculator';
import Icon from '@/components/ui/AppIcon';

interface VehicleFeature {
  icon: string;
  label: string;
}

interface VehicleSpec {
  label: string;
  value: string;
}

interface Vehicle {
  id: string;
  name: string;
  category: string;
  image: string;
  alt: string;
  images: Array<{url: string;alt: string;}>;
  capacity: number;
  luggage: number;
  features: VehicleFeature[];
  specifications: VehicleSpec[];
  priceFrom: number;
  description: string;
  longDescription: string;
  amenities: string[];
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface QuoteData {
  vehicleType: string;
  serviceType: string;
  hours: number;
  date: string;
  passengers: number;
}

const FleetInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCapacity, setSelectedCapacity] = useState('all');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recommended');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockVehicles: Vehicle[] = [
  {
    id: 'luxury-sedan-1',
    name: 'Mercedes-Benz S-Class',
    category: 'Luxury Sedan',
    image: "https://images.unsplash.com/photo-1632931093439-43a343e5a2a7",
    alt: 'Black Mercedes-Benz S-Class luxury sedan parked in front of modern glass building',
    images: [
    { url: "https://images.unsplash.com/photo-1629019878464-5a7ef877716f", alt: 'Black Mercedes-Benz S-Class exterior front view' },
    { url: "https://images.unsplash.com/photo-1675017252524-9ea7a520609b", alt: 'Mercedes-Benz S-Class luxury leather interior with ambient lighting' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_10f4419c7-1764745532246.png", alt: 'Mercedes-Benz S-Class rear passenger compartment with executive seating' }],

    capacity: 3,
    luggage: 3,
    features: [
    { icon: 'WifiIcon', label: 'Complimentary Wi-Fi' },
    { icon: 'DevicePhoneMobileIcon', label: 'Device Charging Ports' },
    { icon: 'NewspaperIcon', label: 'Newspapers & Magazines' },
    { icon: 'SparklesIcon', label: 'Premium Leather Interior' },
    { icon: 'SunIcon', label: 'Climate Control' }],

    specifications: [
    { label: 'Make & Model', value: 'Mercedes-Benz S-Class' },
    { label: 'Year', value: '2024' },
    { label: 'Color', value: 'Executive Black' },
    { label: 'Transmission', value: 'Automatic' },
    { label: 'Fuel Type', value: 'Premium Gasoline' },
    { label: 'Seating', value: 'Premium Leather' }],

    priceFrom: 85,
    description: 'Executive luxury sedan perfect for airport transfers and business meetings',
    longDescription: 'Experience unparalleled comfort and sophistication in our Mercedes-Benz S-Class. This flagship luxury sedan combines cutting-edge technology with timeless elegance, making it the perfect choice for executives who demand the best. With its whisper-quiet cabin, advanced safety features, and premium amenities, every journey becomes a first-class experience.',
    amenities: ['wifi', 'charging', 'climate', 'leather']
  },
  {
    id: 'executive-suv-1',
    name: 'Cadillac Escalade ESV',
    category: 'Executive SUV',
    image: "https://images.unsplash.com/photo-1716878900688-d080dd08c3ae",
    alt: 'Black Cadillac Escalade ESV luxury SUV with chrome accents on city street',
    images: [
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_174d24853-1766964167513.png", alt: 'Cadillac Escalade ESV exterior side profile' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1681020a2-1764872325196.png", alt: 'Cadillac Escalade ESV spacious interior with captain chairs' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c00aac8e-1764872326261.png", alt: 'Cadillac Escalade ESV entertainment system and rear seating' }],

    capacity: 6,
    luggage: 8,
    features: [
    { icon: 'WifiIcon', label: 'High-Speed Wi-Fi' },
    { icon: 'TvIcon', label: 'Entertainment System' },
    { icon: 'DevicePhoneMobileIcon', label: 'Multiple Charging Stations' },
    { icon: 'SparklesIcon', label: 'Premium Sound System' },
    { icon: 'ShieldCheckIcon', label: 'Advanced Safety Features' }],

    specifications: [
    { label: 'Make & Model', value: 'Cadillac Escalade ESV' },
    { label: 'Year', value: '2024' },
    { label: 'Color', value: 'Midnight Black' },
    { label: 'Transmission', value: 'Automatic' },
    { label: 'Fuel Type', value: 'Premium Gasoline' },
    { label: 'Seating', value: 'Captain Chairs' }],

    priceFrom: 110,
    description: 'Spacious luxury SUV ideal for group travel and family occasions',
    longDescription: 'The Cadillac Escalade ESV represents the pinnacle of American luxury and capability. With its commanding presence and spacious interior, this executive SUV is perfect for group transportation without compromising on comfort or style. Whether you are traveling with colleagues or family, the Escalade ensures everyone arrives in comfort and style.',
    amenities: ['wifi', 'entertainment', 'charging', 'sound']
  },
  {
    id: 'stretch-limo-1',
    name: 'Lincoln Stretch Limousine',
    category: 'Stretch Limousine',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17e2723b5-1766149271631.png",
    alt: 'White Lincoln stretch limousine with tinted windows at elegant venue entrance',
    images: [
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1dbe87642-1766964169536.png", alt: 'Lincoln stretch limousine exterior full length view' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1faf494a5-1766149272918.png", alt: 'Lincoln stretch limousine luxurious interior with bar and seating' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_17e2723b5-1766149271631.png", alt: 'Lincoln stretch limousine entertainment area with mood lighting' }],

    capacity: 10,
    luggage: 4,
    features: [
    { icon: 'SparklesIcon', label: 'Fiber Optic Lighting' },
    { icon: 'MusicalNoteIcon', label: 'Premium Sound System' },
    { icon: 'GlobeAltIcon', label: 'Bar & Refreshments' },
    { icon: 'TvIcon', label: 'Multiple Screens' },
    { icon: 'StarIcon', label: 'Privacy Partition' }],

    specifications: [
    { label: 'Make & Model', value: 'Lincoln Stretch' },
    { label: 'Year', value: '2023' },
    { label: 'Color', value: 'Pearl White' },
    { label: 'Length', value: '120 inches' },
    { label: 'Interior', value: 'Custom Luxury' },
    { label: 'Bar', value: 'Fully Stocked' }],

    priceFrom: 150,
    description: 'Classic stretch limousine for weddings, proms, and special celebrations',
    longDescription: 'Make your special occasion truly unforgettable with our Lincoln Stretch Limousine. This iconic vehicle combines timeless elegance with modern amenities, creating the perfect atmosphere for celebrations. From weddings to proms, anniversaries to nights out, our stretch limo ensures you arrive in style and create memories that last a lifetime.',
    amenities: ['lighting', 'sound', 'bar', 'entertainment']
  },
  {
    id: 'party-bus-1',
    name: 'Executive Party Bus',
    category: 'Party Bus',
    image: "https://images.unsplash.com/photo-1606371563987-afbcd1f65aec",
    alt: 'Black executive party bus with LED lighting and custom exterior graphics',
    images: [
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_12b13a2e0-1766964170184.png", alt: 'Executive party bus exterior with custom paint' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_101f0a20b-1764687280071.png", alt: 'Party bus interior with lounge seating and dance floor' },
    { url: "https://images.unsplash.com/photo-1658112008842-832597490d26", alt: 'Party bus entertainment system with LED lighting' }],

    capacity: 20,
    luggage: 10,
    features: [
    { icon: 'MusicalNoteIcon', label: 'Professional Sound System' },
    { icon: 'SparklesIcon', label: 'LED Dance Floor' },
    { icon: 'GlobeAltIcon', label: 'Full Bar Setup' },
    { icon: 'TvIcon', label: 'Multiple HD Screens' },
    { icon: 'FireIcon', label: 'Climate Controlled' }],

    specifications: [
    { label: 'Type', value: 'Executive Party Bus' },
    { label: 'Year', value: '2024' },
    { label: 'Color', value: 'Midnight Black' },
    { label: 'Length', value: '40 feet' },
    { label: 'Dance Floor', value: 'LED Illuminated' },
    { label: 'Restroom', value: 'Onboard' }],

    priceFrom: 200,
    description: 'Ultimate party experience for large groups and corporate events',
    longDescription: 'Transform your event into an unforgettable experience with our Executive Party Bus. This mobile entertainment venue features everything you need for an incredible celebration on wheels. With its spacious interior, state-of-the-art sound system, and luxurious amenities, our party bus is perfect for bachelor/bachelorette parties, corporate events, or any celebration that deserves to be extraordinary.',
    amenities: ['sound', 'lighting', 'bar', 'entertainment', 'climate']
  },
  {
    id: 'luxury-sedan-2',
    name: 'BMW 7 Series',
    category: 'Luxury Sedan',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1352c3241-1765801718570.png",
    alt: 'Silver BMW 7 Series luxury sedan with sporty design on urban street',
    images: [
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5002831-1766964171289.png", alt: 'BMW 7 Series exterior front three-quarter view' },
    { url: "https://images.unsplash.com/photo-1633123467243-d8c71d749637", alt: 'BMW 7 Series executive interior with digital displays' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1cf0621-1766964169109.png", alt: 'BMW 7 Series rear executive lounge seating' }],

    capacity: 3,
    luggage: 3,
    features: [
    { icon: 'WifiIcon', label: 'Premium Wi-Fi' },
    { icon: 'DevicePhoneMobileIcon', label: 'Wireless Charging' },
    { icon: 'TvIcon', label: 'Rear Entertainment' },
    { icon: 'SparklesIcon', label: 'Massage Seats' },
    { icon: 'SunIcon', label: '4-Zone Climate' }],

    specifications: [
    { label: 'Make & Model', value: 'BMW 7 Series' },
    { label: 'Year', value: '2024' },
    { label: 'Color', value: 'Mineral White' },
    { label: 'Transmission', value: 'Automatic' },
    { label: 'Fuel Type', value: 'Premium Gasoline' },
    { label: 'Seating', value: 'Executive Leather' }],

    priceFrom: 90,
    description: 'German engineering excellence for discerning executives',
    longDescription: 'The BMW 7 Series represents the perfect fusion of performance and luxury. With its innovative technology, refined interior, and dynamic driving characteristics, this executive sedan delivers an unmatched experience. Ideal for business travelers who appreciate both comfort and performance, the 7 Series ensures you arrive refreshed and ready for success.',
    amenities: ['wifi', 'charging', 'entertainment', 'massage']
  },
  {
    id: 'executive-suv-2',
    name: 'Lincoln Navigator',
    category: 'Executive SUV',
    image: "https://images.unsplash.com/photo-1654522650196-732d7974a096",
    alt: 'Black Lincoln Navigator luxury SUV with chrome grille and LED headlights',
    images: [
    { url: "https://images.unsplash.com/photo-1538233018998-e0e22b5b2cc0", alt: 'Lincoln Navigator exterior front view with signature grille' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_15d97bdab-1766807216281.png", alt: 'Lincoln Navigator premium interior with panoramic sunroof' },
    { url: "https://img.rocket.new/generatedImages/rocket_gen_img_1a91871a5-1764645680981.png", alt: 'Lincoln Navigator spacious cargo area and third-row seating' }],

    capacity: 7,
    luggage: 6,
    features: [
    { icon: 'WifiIcon', label: '5G Wi-Fi Hotspot' },
    { icon: 'TvIcon', label: 'Rear Seat Entertainment' },
    { icon: 'DevicePhoneMobileIcon', label: 'USB-C Charging' },
    { icon: 'MusicalNoteIcon', label: 'Revel Audio System' },
    { icon: 'SunIcon', label: 'Panoramic Sunroof' }],

    specifications: [
    { label: 'Make & Model', value: 'Lincoln Navigator' },
    { label: 'Year', value: '2024' },
    { label: 'Color', value: 'Infinite Black' },
    { label: 'Transmission', value: 'Automatic' },
    { label: 'Fuel Type', value: 'Premium Gasoline' },
    { label: 'Seating', value: 'Perfect Position Seats' }],

    priceFrom: 115,
    description: 'American luxury SUV with unmatched comfort and technology',
    longDescription: 'The Lincoln Navigator redefines luxury SUV travel with its spacious cabin, advanced technology, and refined comfort. Perfect for executive teams or families who refuse to compromise on luxury, the Navigator offers a sanctuary on wheels. With its commanding presence and thoughtful amenities, every journey becomes a first-class experience.',
    amenities: ['wifi', 'entertainment', 'charging', 'sound', 'sunroof']
  }];


  const categories: FilterOption[] = [
  { id: 'all', label: 'All Vehicles', count: mockVehicles.length },
  { id: 'Luxury Sedan', label: 'Luxury Sedans', count: 2 },
  { id: 'Executive SUV', label: 'Executive SUVs', count: 2 },
  { id: 'Stretch Limousine', label: 'Stretch Limousines', count: 1 },
  { id: 'Party Bus', label: 'Party Buses', count: 1 }];


  const capacities: FilterOption[] = [
  { id: 'all', label: 'Any Capacity', count: mockVehicles.length },
  { id: '1-3', label: '1-3 Passengers', count: 2 },
  { id: '4-7', label: '4-7 Passengers', count: 2 },
  { id: '8-15', label: '8-15 Passengers', count: 1 },
  { id: '16+', label: '16+ Passengers', count: 1 }];


  const priceRanges: FilterOption[] = [
  { id: 'all', label: 'All Prices', count: mockVehicles.length },
  { id: '0-100', label: '$0 - $100/hour', count: 2 },
  { id: '101-150', label: '$101 - $150/hour', count: 2 },
  { id: '151-200', label: '$151 - $200/hour', count: 1 },
  { id: '201+', label: '$201+/hour', count: 1 }];


  const amenities: FilterOption[] = [
  { id: 'wifi', label: 'Wi-Fi', count: 6 },
  { id: 'entertainment', label: 'Entertainment System', count: 4 },
  { id: 'charging', label: 'Device Charging', count: 6 },
  { id: 'bar', label: 'Bar Service', count: 2 },
  { id: 'sound', label: 'Premium Sound', count: 4 },
  { id: 'lighting', label: 'Ambient Lighting', count: 2 }];


  const filterVehicles = () => {
    let filtered = [...mockVehicles];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((v) => v.category === selectedCategory);
    }

    if (selectedCapacity !== 'all') {
      const [min, max] = selectedCapacity.split('-').map((n) => n === '+' ? 999 : parseInt(n));
      filtered = filtered.filter((v) => v.capacity >= min && (max ? v.capacity <= max : true));
    }

    if (selectedPriceRange !== 'all') {
      const [min, max] = selectedPriceRange.split('-').map((n) => n === '+' ? 9999 : parseInt(n));
      filtered = filtered.filter((v) => v.priceFrom >= min && (max ? v.priceFrom <= max : true));
    }

    if (selectedAmenities.length > 0) {
      filtered = filtered.filter((v) =>
      selectedAmenities.every((amenity) => v.amenities.includes(amenity))
      );
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (v) =>
        v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.priceFrom - b.priceFrom);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.priceFrom - a.priceFrom);
    } else if (sortBy === 'capacity') {
      filtered.sort((a, b) => b.capacity - a.capacity);
    }

    return filtered;
  };

  const filteredVehicles = isHydrated ? filterVehicles() : mockVehicles;

  const handleViewDetails = (id: string) => {
    const vehicle = mockVehicles.find((v) => v.id === id);
    if (vehicle) {
      setSelectedVehicle(vehicle);
      setIsModalOpen(true);
    }
  };

  const handleQuickBook = (id: string) => {
    router.push(`/booking-system?vehicle=${id}`);
  };

  const handleBookNow = (id: string) => {
    router.push(`/booking-system?vehicle=${id}`);
  };

  const handleGetQuote = (data: QuoteData) => {
    router.push(
      `/booking-system?vehicleType=${data.vehicleType}&serviceType=${data.serviceType}&hours=${data.hours}&date=${data.date}&passengers=${data.passengers}`
    );
  };

  const handleClearFilters = () => {
    setSelectedCategory('all');
    setSelectedCapacity('all');
    setSelectedPriceRange('all');
    setSelectedAmenities([]);
    setSearchQuery('');
  };

  const handleAmenityToggle = (amenity: string) => {
    setSelectedAmenities((prev) =>
    prev.includes(amenity) ? prev.filter((a) => a !== amenity) : [...prev, amenity]
    );
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:384:6" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="384" data-component-end-line="397" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-16%22%7D" className="min-h-screen bg-background pt-24 pb-16">
        <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:385:8" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="385" data-component-end-line="396" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:386:10" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="386" data-component-end-line="395" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:387:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="387" data-component-end-line="387" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-12 bg-muted rounded w-1/3"></div>
            <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:388:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="388" data-component-end-line="394" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-4%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:389:14" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="389" data-component-end-line="389" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded%22%7D" className="h-96 bg-muted rounded"></div>
              <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:390:14" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="390" data-component-end-line="393" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-3%20space-y-6%22%7D" className="lg:col-span-3 space-y-6">
                <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:391:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="391" data-component-end-line="391" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded%22%7D" className="h-64 bg-muted rounded"></div>
                <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:392:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="392" data-component-end-line="392" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded%22%7D" className="h-64 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:402:4" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="402" data-component-end-line="504" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-16%22%7D" className="min-h-screen bg-background pt-24 pb-16">
      <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:403:6" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="403" data-component-end-line="496" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:404:8" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="404" data-component-end-line="419" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
          <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:405:10" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="405" data-component-end-line="418" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
            <input data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:406:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="406" data-component-end-line="411" data-component-file="FleetInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20px-6%20py-4%20pl-12%20bg-card%20border%20border-border%20rounded-lg%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%22%7D"
              type="text"
              placeholder="Search vehicles by name or features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 pl-12 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />

            <Icon data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:413:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="413" data-component-end-line="416" data-component-file="FleetInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-4%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
              name="MagnifyingGlassIcon"
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />

          </div>
        </div>

        <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:421:8" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="421" data-component-end-line="495" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-4%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:422:10" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="422" data-component-end-line="438" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-1%22%7D" className="lg:col-span-1">
            <FilterSidebar data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:423:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="423" data-component-end-line="436" data-component-file="FleetInteractive.tsx" data-component-name="FilterSidebar" data-component-content="%7B%22elementName%22%3A%22FilterSidebar%22%7D"
              categories={categories}
              capacities={capacities}
              priceRanges={priceRanges}
              amenities={amenities}
              selectedCategory={selectedCategory}
              selectedCapacity={selectedCapacity}
              selectedPriceRange={selectedPriceRange}
              selectedAmenities={selectedAmenities}
              onCategoryChange={setSelectedCategory}
              onCapacityChange={setSelectedCapacity}
              onPriceRangeChange={setSelectedPriceRange}
              onAmenityToggle={handleAmenityToggle}
              onClearFilters={handleClearFilters} />

          </div>

          <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:440:10" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="440" data-component-end-line="494" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-3%20space-y-8%22%7D" className="lg:col-span-3 space-y-8">
            <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:441:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="441" data-component-end-line="460" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
              <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:442:14" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="442" data-component-end-line="449" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <h2 data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:443:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="443" data-component-end-line="445" data-component-file="FleetInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-2xl%20text-primary%22%2C%22textContent%22%3A%22Available%22%7D" className="font-playfair font-bold text-2xl text-primary">
                  {filteredVehicles.length} {filteredVehicles.length === 1 ? 'Vehicle' : 'Vehicles'} Available
                </h2>
                <p data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:446:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="446" data-component-end-line="448" data-component-file="FleetInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-1%22%2C%22textContent%22%3A%22Find%20the%20perfect%20vehicle%20for%20your%20journey%22%7D" className="text-sm text-muted-foreground mt-1">
                  Find the perfect vehicle for your journey
                </p>
              </div>
              <select data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:450:14" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="450" data-component-end-line="459" data-component-file="FleetInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22value%22%3A%22%5Bvar%3AsortBy%5D%22%2C%22className%22%3A%22px-4%20py-2%20bg-card%20border%20border-border%20rounded-md%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%22%7D"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-card border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary">

                <option data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:455:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="455" data-component-end-line="455" data-component-file="FleetInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22recommended%22%2C%22textContent%22%3A%22Recommended%22%7D" value="recommended">Recommended</option>
                <option data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:456:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="456" data-component-end-line="456" data-component-file="FleetInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22price-low%22%2C%22textContent%22%3A%22Price%3A%20Low%20to%20High%22%7D" value="price-low">Price: Low to High</option>
                <option data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:457:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="457" data-component-end-line="457" data-component-file="FleetInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22price-high%22%2C%22textContent%22%3A%22Price%3A%20High%20to%20Low%22%7D" value="price-high">Price: High to Low</option>
                <option data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:458:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="458" data-component-end-line="458" data-component-file="FleetInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22capacity%22%2C%22textContent%22%3A%22Capacity%3A%20High%20to%20Low%22%7D" value="capacity">Capacity: High to Low</option>
              </select>
            </div>

            <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:462:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="462" data-component-end-line="471" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredVehicles.map((vehicle) =>
              <VehicleCard data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:464:14" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="464" data-component-end-line="468" data-component-file="FleetInteractive.tsx" data-component-name="VehicleCard" data-component-content="%7B%22elementName%22%3A%22VehicleCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                key={vehicle.id}
                {...vehicle}
                onViewDetails={handleViewDetails}
                onQuickBook={handleQuickBook} />

              )}
            </div>

            {filteredVehicles.length === 0 &&
            <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:474:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="474" data-component-end-line="488" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
                <Icon data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:475:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="475" data-component-end-line="475" data-component-file="FleetInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22mx-auto%20text-muted-foreground%20mb-4%22%7D" name="MagnifyingGlassIcon" size={64} className="mx-auto text-muted-foreground mb-4" />
                <h3 data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:476:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="476" data-component-end-line="478" data-component-file="FleetInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-2xl%20text-primary%20mb-2%22%2C%22textContent%22%3A%22No%20Vehicles%20Found%22%7D" className="font-playfair font-bold text-2xl text-primary mb-2">
                  No Vehicles Found
                </h3>
                <p data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:479:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="479" data-component-end-line="481" data-component-file="FleetInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20mb-6%22%2C%22textContent%22%3A%22Try%20adjusting%20your%20filters%20or%20search%20criteria%22%7D" className="text-muted-foreground mb-6">
                  Try adjusting your filters or search criteria
                </p>
                <button data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:482:16" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="482" data-component-end-line="487" data-component-file="FleetInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20bg-primary%20text-primary-foreground%20font-montserrat%20font-semibold%20rounded-md%20transition-smooth%20hover%3Abg-opacity-90%22%2C%22textContent%22%3A%22Clear%20All%20Filters%22%7D"
                onClick={handleClearFilters}
                className="px-6 py-3 bg-primary text-primary-foreground font-montserrat font-semibold rounded-md transition-smooth hover:bg-opacity-90">

                  Clear All Filters
                </button>
              </div>
            }

            <div data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:491:12" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="491" data-component-end-line="493" data-component-file="FleetInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-8%22%7D" className="mt-8">
              <QuickQuoteCalculator data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:492:14" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="492" data-component-end-line="492" data-component-file="FleetInteractive.tsx" data-component-name="QuickQuoteCalculator" data-component-content="%7B%22elementName%22%3A%22QuickQuoteCalculator%22%7D" onGetQuote={handleGetQuote} />
            </div>
          </div>
        </div>
      </div>

      <VehicleDetailModal data-component-id="src/app/fleet-and-services/components/FleetInteractive.tsx:498:6" data-component-path="src/app/fleet-and-services/components/FleetInteractive.tsx" data-component-line="498" data-component-end-line="502" data-component-file="FleetInteractive.tsx" data-component-name="VehicleDetailModal" data-component-content="%7B%22elementName%22%3A%22VehicleDetailModal%22%7D"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        vehicle={selectedVehicle}
        onBookNow={handleBookNow} />

    </div>);

};

export default FleetInteractive;